import type { PatientApplyProfileData } from '@modules/patientApplyProfile/helpers/PatientApplyProfileData'
import type { PatientTrialApplySurveyAttribute } from './types'

/**
 * Does the attribute (e.g. name, DoB) exist in the patient profile data?
 * @property {PatientTrialApplySurveyAttribute} attribute - Attribute to check for
 * @property {PatientApplyProfileData} patientProfileData - Patient profile to search in
 * @returns {boolean} Whether the attribute is present in the patient profile data
 */
export function attributeIsPresentInPatientProfile({
  attribute,
  patientProfileData,
  skippable,
}: {
  attribute: PatientTrialApplySurveyAttribute
  patientProfileData: PatientApplyProfileData
  skippable?: boolean
}) {
  const attributeValue = getAttributeDataFromPatientProfileData({
    attribute,
    patientProfileData,
  })
  const attributeValueIsPresent = attributeValue.length > 0

  if (skippable) {
    return attributeValueIsPresent
  }

  return (
    attributeValueIsPresent &&
    attributeValue.every((v) => v && v.toString().trim().length > 0)
  )
}

/**
 * Fetch the value for the attribute in the given patient profile
 * @property {PatientTrialApplySurveyAttribute} attribute - Attribute to check for
 * @property {PatientApplyProfileData} patientProfileData - Patient profile to search in
 * @returns {string[] | null} Attribute value, or null if not found
 */
export function getAttributeDataFromPatientProfileData({
  attribute,
  patientProfileData,
}: {
  attribute: PatientTrialApplySurveyAttribute
  patientProfileData: PatientApplyProfileData
}) {
  return patientProfileData
    .filter((d) => d.conceptName === attribute)
    .map((d) => d.conceptValue)
}
